import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// --- Automatic token refresh on 401 ---
let isRefreshing = false;
let refreshQueue = [];

function processQueue(error) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  refreshQueue = [];
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Skip refresh for auth endpoints themselves
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/register')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Redirect to login on failed refresh — but NOT for public pages
        const isPublicPage = ['/portal', '/how-it-works', '/pricing', '/login', '/register'].some(
          p => window.location.pathname.startsWith(p)
        );
        if (!isPublicPage) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Intercept every request to inject the current active client header.
// Always read from localStorage for the freshest value (avoids React effect race conditions).
axios.interceptors.request.use((config) => {
  const slug = localStorage.getItem('activeClientSlug');
  if (slug) config.headers['X-Client-Id'] = slug;
  return config;
});

// --- Authenticated fetch with auto-refresh on 401 ---
export async function authFetch(url, options = {}) {
  let resp = await fetch(url, { credentials: 'include', ...options });
  if (resp.status === 401) {
    try {
      await fetch(`${API}/auth/refresh`, { method: 'POST', credentials: 'include' });
      resp = await fetch(url, { credentials: 'include', ...options });
    } catch { /* refresh failed */ }
  }
  return resp;
}

// Posts API
export const postsApi = {
  getAll: async (month, year) => {
    const params = {};
    if (month) params.month = month;
    if (year) params.year = year;
    const { data } = await axios.get(`${API}/posts`, { params, withCredentials: true });
    return data;
  },

  getOne: async (id) => {
    const { data } = await axios.get(`${API}/posts/${id}`, { withCredentials: true });
    return data;
  },

  create: async (postData) => {
    const { data } = await axios.post(`${API}/posts`, postData, { withCredentials: true });
    return data;
  },

  update: async (id, postData) => {
    const { data } = await axios.put(`${API}/posts/${id}`, postData, { withCredentials: true });
    return data;
  },

  delete: async (id) => {
    const { data } = await axios.delete(`${API}/posts/${id}`, { withCredentials: true });
    return data;
  },

  duplicate: async (id, scheduledDate) => {
    const url = scheduledDate 
      ? `${API}/posts/${id}/duplicate?scheduled_date=${scheduledDate}`
      : `${API}/posts/${id}/duplicate`;
    const { data } = await axios.post(url, {}, { withCredentials: true });
    return data;
  },

  getUpcoming: async (days = 7) => {
    const { data } = await axios.get(`${API}/posts/upcoming`, { params: { days }, withCredentials: true });
    return data;
  },

  list: async () => {
    const { data } = await axios.get(`${API}/posts`, { withCredentials: true });
    return data;
  },

  updateWorkflow: async (id, workflowStatus) => {
    const { data } = await axios.put(`${API}/posts/${id}/workflow?workflow_status=${workflowStatus}`, {}, { withCredentials: true });
    return data;
  }
};

// Dashboard API
export const dashboardApi = {
  getStats: async () => {
    const { data } = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
    return data;
  }
};

// Files API
// Helper to get active client ID for non-axios calls
function getActiveClientId() {
  return axios.defaults.headers.common['X-Client-Id'] || localStorage.getItem('activeClientSlug') || '';
}

export const filesApi = {
  upload: async (file) => {
    // Use chunked upload for files > 8MB
    if (file.size > 8 * 1024 * 1024) {
      return filesApi.uploadChunked(file);
    }
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axios.post(`${API}/upload`, formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000
    });
    return data;
  },

  uploadChunked: async (file) => {
    const CHUNK_SIZE = 4 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    // Init
    const initResp = await axios.post(`${API}/library/upload/init`, {
      filename: file.name,
      totalSize: file.size,
      totalChunks,
      category: 'General',
    }, { withCredentials: true });
    const { upload_id } = initResp.data;

    // Upload chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const formData = new FormData();
      formData.append('file', chunk, `chunk_${i}`);
      await axios.post(`${API}/library/upload/chunk/${upload_id}?chunk_index=${i}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
    }

    // Complete
    await axios.post(`${API}/library/upload/complete/${upload_id}`, {}, {
      withCredentials: true,
      timeout: 30000,
    });

    // Poll for background assembly completion
    const pollInterval = 2000;
    const maxPolls = 300;
    for (let p = 0; p < maxPolls; p++) {
      await new Promise(r => setTimeout(r, pollInterval));
      const statusResp = await axios.get(`${API}/library/upload/status/${upload_id}`, {
        withCredentials: true,
        timeout: 15000,
      });
      const st = statusResp.data;
      if (st.status === 'completed') return st;
      if (st.status === 'failed') throw new Error(st.error || 'Assembly failed');
    }
    throw new Error('Upload processing timed out');
  },

  get: async (fileId) => {
    try {
      // Handle different URL formats
      let url;
      if (fileId.startsWith('/api/')) {
        url = `${API}${fileId.replace('/api/', '/')}`;
      } else if (fileId.startsWith('http')) {
        url = fileId;
      } else {
        url = `${API}/files/${fileId}`;
      }
      const response = await authFetch(url, {
        headers: { 'X-Client-Id': getActiveClientId() }
      });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }
};

// Import API
export const importApi = {
  csv: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axios.post(`${API}/import/csv`, formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  },

  preview: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axios.post(`${API}/import/preview`, formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  },

  spreadsheet: async (file, weekStartDate) => {
    const formData = new FormData();
    formData.append('file', file);
    let url = `${API}/import/spreadsheet`;
    if (weekStartDate) {
      url += `?week_start_date=${weekStartDate}T10:00:00`;
    }
    const { data } = await axios.post(url, formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  }
};
